import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowRight, Phone, Star, Gem, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
const marbleBg = "/images/marble-bg-optimized.jpg";

// Videos served directly from /public/videos for fast streaming (no JS bundle)
const videos = [
  { src: "/videos/showcase-7.mp4", poster: "/videos/posters/showcase-7.jpg" },
  { src: "/videos/showcase-2.mp4", poster: "/videos/posters/showcase-2.jpg" },
  { src: "/videos/showcase-3.mp4", poster: "/videos/posters/showcase-3.jpg" },
  { src: "/videos/showcase-4.mp4", poster: "/videos/posters/showcase-4.jpg" },
  { src: "/videos/showcase-5.mp4", poster: "/videos/posters/showcase-5.jpg" },
  { src: "/videos/showcase-6.mp4", poster: "/videos/posters/showcase-6.jpg" },
];

// Lazy video component: poster paints instantly, src loads on viewport enter.
// All six videos play on every device (they are the hero's signature). To keep
// mobile smooth we only decode while visible: playback is paused when the tile
// scrolls out of view, so the phone never juggles six active decodes at once.
function LazyVideo({ index, src, poster }: { index: number; src: string; poster: string }) {
  const [shouldLoad, setShouldLoad] = useState(index < 2); // eager-load first 2
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoElRef = useRef<HTMLVideoElement>(null);

  // Load when near viewport
  useEffect(() => {
    if (shouldLoad) return;
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad]);

  // Play only while visible to keep decode cost low on phones
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const v = videoElRef.current;
        if (!v) return;
        if (entry.isIntersecting) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <div
      ref={wrapperRef}
      className="relative aspect-[9/16] rounded-xl overflow-hidden group cursor-pointer bg-background/30"
      style={{
        boxShadow: "0 10px 30px hsla(200 60% 20% / 0.3)",
        contain: "layout paint",
      }}
    >
      <video
        ref={videoElRef}
        src={shouldLoad ? src : undefined}
        poster={poster}
        aria-label="Tile, grout and stone restoration project by Lazona Tile Care"
        className="w-full h-full object-cover md:transition-transform md:duration-500 md:group-hover:scale-105"
        style={{ transform: "translateZ(0)" }}
        autoPlay
        muted
        loop
        playsInline
        preload={index < 2 ? "auto" : "metadata"}
        onLoadedData={() => {
          videoElRef.current?.play().catch(() => {});
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent opacity-0 md:group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );
}



export function MarbleHero() {
  const ref = useRef<HTMLElement>(null);
  const lightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = ref.current;
    const light = lightRef.current;
    if (!section || !light) return;
    if (window.matchMedia("(max-width: 767px)").matches) return;

    let raf = 0;
    let tx = 50, ty = 35;
    const onMove = (e: MouseEvent) => {
      const r = section.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width) * 100;
      ty = ((e.clientY - r.top) / r.height) * 100;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          light.style.setProperty("--mx", `${tx}%`);
          light.style.setProperty("--my", `${ty}%`);
          raf = 0;
        });
      }
    };
    section.addEventListener("mousemove", onMove);
    return () => {
      section.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={ref}
      className="relative flex flex-col items-center overflow-hidden marble-hero md:min-h-screen"
    >
      {/* Marble Background - LCP-optimized <img> tag for Lighthouse detection */}
      <img 
        src={marbleBg}
        alt="Professional tile and stone restoration in Arizona"
        width={1344}
        height={764}
        fetchPriority="high"
        decoding="async"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          filter: 'contrast(1.22) saturate(1.45) brightness(1.05)',
        }}
      />

      {/* Overlays for text readability */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        {/* Soft top vignette for header legibility ,  kept light so marble vibrance reads through */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, hsla(200 55% 14% / 0.55) 0%, hsla(200 45% 18% / 0.25) 35%, hsla(200 50% 14% / 0.55) 100%)',
          }}
        />

        {/* Backlit marble ,  mouse-reactive glow rendered THROUGH the stone (overlay blend) */}
        <div
          ref={lightRef}
          className="marble-backlight absolute inset-0"
          style={{ ['--mx' as string]: '50%', ['--my' as string]: '40%' }}
        />

        {/* Warm under-slab glow rising from below, like a backlit countertop edge */}
        <div className="marble-underglow absolute inset-x-0 bottom-0 h-2/3" />

        {/* Slow drifting luminous veins ,  soft-light so they appear to ignite the stone's own veining */}
        <div className="marble-veins absolute inset-0" />

        {/* Specular grazing sheen across polished surface */}
        <div className="marble-sheen absolute inset-0" />

        {/* Cool ambient halos ,  kept subtle */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-[0.18]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/15 rounded-full blur-3xl opacity-[0.16]" />
      </div>

      <div 
        className="container-custom relative z-10 pt-44 md:pt-40 pb-16 flex flex-col items-center"
      >
        
        {/* Hero Content */}
        <div className="max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <p
              className="font-display font-bold uppercase tracking-[0.32em] mb-4 inline-flex items-center gap-3 text-base sm:text-lg md:text-xl lg:text-2xl"
              style={{
                background:
                  'linear-gradient(180deg, hsl(45 95% 78%) 0%, hsl(42 90% 60%) 50%, hsl(36 80% 45%) 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                textShadow: '0 0 24px hsla(42 95% 60% / 0.35), 0 2px 8px hsla(0 0% 0% / 0.45)',
                filter: 'drop-shadow(0 0 12px hsla(42 95% 60% / 0.25))',
              }}
            >
              <span aria-hidden className="h-px w-8 sm:w-10 bg-gradient-to-r from-transparent to-accent" />
              Never Seal Your Grout Again
              <span aria-hidden className="h-px w-8 sm:w-10 bg-gradient-to-l from-transparent to-accent" />
            </p>
            <h1 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extralight tracking-tight text-foreground mb-6 leading-tight">
              <span className="sr-only">Lazona Tile Care – </span>
              Arizona's Premier Tile, Grout & Stone Floor, Counter & Shower{" "}
              <span className="text-primary">Restoration</span>{" "}
              &{" "}
              <span className="text-primary">Preservation</span>{" "}
              <span 
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 400,
                  fontStyle: 'italic',
                  color: 'hsl(40 70% 55%)',
                }}
              >
                Specialists
              </span>
            </h1>
          </motion.div>

          {/* Video Showcase - at top of hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="w-full max-w-6xl mx-auto mb-10"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {videos.map((v, index) => (
                <LazyVideo key={v.src} index={index} src={v.src} poster={v.poster} />
              ))}
            </div>
          </motion.div>


          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4"
          >
            <Button
              size="lg"
              asChild
              className="btn-backlit btn-backlit-gold group min-w-[240px] h-14 rounded-2xl font-display text-base md:text-lg font-semibold tracking-tight
                bg-gradient-to-b from-primary via-primary to-primary/80 text-primary-foreground
                shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.35),inset_0_-2px_0_hsl(0_0%_0%/0.25),0_10px_24px_-8px_hsl(var(--primary)/0.55),0_2px_0_hsl(0_0%_0%/0.2)]
                hover:shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.45),inset_0_-2px_0_hsl(0_0%_0%/0.3),0_14px_30px_-8px_hsl(var(--primary)/0.65),0_3px_0_hsl(0_0%_0%/0.22)]
                hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(0_0%_0%/0.3)]
                transition-all duration-200 ease-out border border-primary/40"
            >
              <a href="tel:4803520392">
                <Phone className="w-4 h-4" />
                CALL NOW
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <Button
              size="lg"
              asChild
              className="btn-backlit min-w-[240px] h-14 rounded-2xl font-display text-base md:text-lg font-semibold tracking-tight
                bg-gradient-to-b from-background via-background to-muted text-foreground
                border border-primary/30
                shadow-[inset_0_1px_0_hsl(var(--foreground)/0.06),inset_0_-2px_0_hsl(var(--primary)/0.18),0_8px_20px_-8px_hsl(var(--primary)/0.35),0_2px_0_hsl(var(--primary)/0.15)]
                hover:shadow-[inset_0_1px_0_hsl(var(--foreground)/0.08),inset_0_-2px_0_hsl(var(--primary)/0.25),0_12px_26px_-8px_hsl(var(--primary)/0.45),0_3px_0_hsl(var(--primary)/0.18)]
                hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(var(--primary)/0.2)]
                transition-all duration-200 ease-out"
            >
              <a href="/contact">
                Book A Consultation
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.15 }}
            className="text-center text-sm text-muted-foreground mb-10"
          >
            <span className="text-accent font-semibold">$150 consultations</span>  Grout color sealing comes with a 15-year warranty for homeowners. It covers the color sealer, not the tile, the installation underneath or other surfaces. Commercial work is not covered by the 15-year warranty. </motion.p>


          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="flex flex-wrap justify-center gap-6 md:gap-8"
          >
            {[
              { icon: Star, text: "5.0 Google Rating" },
              { icon: Gem, text: "Stone & Tile Specialists" },
            ].map((badge) => (
              <div key={badge.text} className="flex items-center gap-2 text-foreground">
                <badge.icon className="w-5 h-5 text-accent" />
                <span className="font-display text-sm sm:text-base font-semibold tracking-tight">{badge.text}</span>
              </div>
            ))}
          </motion.div>

          {/* Blog Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="flex justify-center mt-8"
          >
            <Link to="/blog">
              <Button
                size="lg"
                className="btn-backlit btn-backlit-gold group min-w-[220px] h-14 rounded-2xl font-display text-base md:text-lg font-semibold tracking-tight
                  bg-gradient-to-b from-accent via-accent to-accent/75 text-accent-foreground
                  border border-accent/50
                  shadow-[inset_0_1px_0_hsl(var(--accent-foreground)/0.4),inset_0_-2px_0_hsl(0_0%_0%/0.25),0_10px_26px_-8px_hsl(var(--accent)/0.6),0_2px_0_hsl(0_0%_0%/0.2)]
                  hover:shadow-[inset_0_1px_0_hsl(var(--accent-foreground)/0.5),inset_0_-2px_0_hsl(0_0%_0%/0.3),0_14px_32px_-8px_hsl(var(--accent)/0.7),0_3px_0_hsl(0_0%_0%/0.22)]
                  hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_hsl(0_0%_0%/0.3)]
                  transition-all duration-200 ease-out"
              >
                <BookOpen className="w-4 h-4" />
                Explore Our Blog
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
